import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banner, setBanner] = useState(null);
  const [email, setEmail] = useState("");
  const { addItem } = useCart();

  useEffect(() => {
    api.get("/products?featured=true&limit=8").then(({ data }) => setFeatured(data));
    api.get("/categories").then(({ data }) => setCategories(data));
    api.get("/banners?position=hero").then(({ data }) => setBanner(data[0] || null));
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const { data } = await api.post("/newsletter/subscribe", { email });
      toast.success(data.message);
      setEmail("");
    } catch { toast.error("No pudimos suscribirte"); }
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative grain bg-[#F9F6F0]">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 pt-12 pb-20 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-6 lg:pb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#A94438]">Colección Cálido Otoño</p>
            <h1 className="mt-4 font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-[#2C302E]">
              Hecho con<br/>manos<br/><em className="text-[#A94438] not-italic">amorosas.</em>
            </h1>
            <p className="mt-6 max-w-md text-base text-[#5B605E]">
              Ropa, accesorios y regalos de edición limitada para celebrar
              lo cotidiano y volverlo extraordinario.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/catalogo" className="btn-primary" data-testid="hero-cta-shop">Comprar ahora</Link>
              <Link to="/catalogo/regalos" className="btn-ghost" data-testid="hero-cta-gifts">Ver regalos →</Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative">
              <img
                src={banner?.image_url || "https://images.pexels.com/photos/13183926/pexels-photo-13183926.png"}
                alt="Hero"
                className="w-full h-[560px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#F9F6F0] border border-[#E8E3DA] px-6 py-4 hidden md:block">
                <p className="text-xs uppercase tracking-widest text-[#5B605E]">Edición limitada</p>
                <p className="font-serif text-xl tracking-tighter">12 piezas únicas</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 py-24" data-testid="categories-section">
        <div className="flex items-end justify-between mb-12">
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter">Explora por mundo</h2>
          <Link to="/catalogo" className="link-underline text-sm uppercase tracking-wider hidden sm:inline">Ver todo</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {categories.map((c, i) => (
            <Link key={c.id} to={`/catalogo/${c.slug}`} className={`group relative overflow-hidden ${i === 1 ? "md:translate-y-12" : ""}`} data-testid={`cat-${c.slug}`}>
              <div className="aspect-[4/5] overflow-hidden bg-[#F2EFE9]">
                <img src={c.image_url} alt={c.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/50 to-transparent text-[#F9F6F0]">
                <p className="text-xs uppercase tracking-[0.3em]">Colección</p>
                <h3 className="font-serif text-3xl tracking-tighter">{c.name}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="bg-[#F2EFE9] py-24" data-testid="featured-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#A94438]">Lo más querido</p>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter">Favoritos de la semana</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} onAdd={(prod) => { addItem(prod); toast.success(`${prod.name} añadido a la bolsa`); }} />
            ))}
          </div>
        </div>
      </section>

      {/* MARKETING / NEWSLETTER */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 py-24 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter">Cartas que te llegan al corazón.</h2>
          <p className="mt-4 text-[#5B605E] max-w-md">
            Suscríbete y recibe <strong>10% de descuento</strong> en tu primera compra (código <em>BIENVENIDA10</em>),
            avisos de ediciones limitadas y guías de regalo.
          </p>
          <form onSubmit={subscribe} className="mt-6 flex gap-3 max-w-md" data-testid="newsletter-form">
            <input
              data-testid="newsletter-email"
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@correo.com"
              className="flex-1 bg-transparent border-b border-[#2C302E] py-3 px-1 focus:outline-none"
            />
            <button data-testid="newsletter-submit" className="btn-primary">Suscribirme</button>
          </form>
        </div>
        <div className="relative aspect-square overflow-hidden">
          <img src="https://images.pexels.com/photos/8165653/pexels-photo-8165653.jpeg" alt="Accesorios" className="h-full w-full object-cover" />
        </div>
      </section>
    </div>
  );
}
